import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, RadioIcon, CheckIcon, XIcon } from 'lucide-react';
import { type PhysicalReader, type LogicalReader, type Door } from '../App';
type ReaderItemProps = {
  reader: PhysicalReader;
  logicalReaders: LogicalReader[];
  doors: Door[];
  onAssignReader: (serialNumber: string, logicalReaderId: string | null) => void;
  onCreateReader: (serialNumber?: string) => void;
  onPingReader: (serialNumber: string) => void;
  onConfirmReader: (serialNumber: string) => void;
  onStopBlinking: (serialNumber: string) => void;
};
const ReaderItem = ({
  reader,
  logicalReaders,
  doors,
  onAssignReader,
  onCreateReader,
  onPingReader,
  onConfirmReader,
  onStopBlinking
}: ReaderItemProps) => {
  const [expanded, setExpanded] = useState(false);
  // Find the assigned logical reader and door
  const assignedReader = logicalReaders.find(lr => lr.id === reader.logicalReaderId);
  const assignedDoor = doors.find(d => d.id === reader.doorId);
  const handleAssignReader = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    // Only allow assignment if the reader is confirmed
    if (reader.isConfirmed) {
      onAssignReader(reader.serialNumber, value === '' ? null : value);
    } else {
      alert('Please ping and confirm the reader first before making changes.');
      e.target.value = reader.logicalReaderId || '';
    }
  };
  // Handler for the inline create button
  const handleCreateClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expanding/collapsing the item
    // Only allow creation if the reader is confirmed
    if (reader.isConfirmed) {
      onCreateReader(reader.serialNumber);
    } else {
      alert('Please ping and confirm the reader first before creating a new logical reader.');
    }
  };
  // Handler for the ping button
  const handlePingClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expanding/collapsing the item
    onPingReader(reader.serialNumber);
  };
  // Handler for confirming the reader is blinking
  const handleConfirmClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expanding/collapsing the item
    onConfirmReader(reader.serialNumber);
  };
  // Handler for stopping the blinking
  const handleStopBlinkingClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expanding/collapsing the item
    onStopBlinking(reader.serialNumber);
  };
  return <div className={`bg-white rounded-lg shadow-sm border ${reader.isBlinking ? 'border-yellow-400 animate-pulse' : 'border-gray-200'} overflow-hidden`} data-id="element-44">
      {/* Header section always visible */}
      <div className="p-4 flex justify-between items-center cursor-pointer" onClick={() => setExpanded(!expanded)} data-id="element-45">
        <div className="flex-1" data-id="element-46">
          <div className="flex items-center" data-id="element-47">
            <h3 className="font-medium text-gray-900" data-id="element-48">{reader.serialNumber}</h3>
            {reader.isConfirmed && <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full" data-id="element-49">
                Verified
              </span>}
          </div>
          {assignedReader ? <p className="text-sm text-gray-600" data-id="element-50">
              Assigned to: {assignedReader.name}
            </p> : <div className="flex items-center justify-between" data-id="element-51">
              <p className="text-sm text-gray-600 mr-2" data-id="element-52">
                Not assigned to any logical reader
              </p>
              <button onClick={handleCreateClick} disabled={!reader.isConfirmed} className={`flex items-center ${reader.isConfirmed ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'} text-white px-2 py-1 rounded text-xs font-medium`} data-id="element-53">
                <PlusIcon size={14} className="mr-1" data-id="element-54" />
                Register New Reader
              </button>
            </div>}
        </div>
        <button className="text-gray-500 ml-2" data-id="element-55">
          {expanded ? <ChevronUpIcon size={20} data-id="element-56" /> : <ChevronDownIcon size={20} data-id="element-57" />}
        </button>
      </div>
      {/* Ping and Confirm section */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50" data-id="element-58">
        <div className="flex items-center justify-between" data-id="element-59">
          <div className="flex-1" data-id="element-60">
            {reader.isBlinking ? reader.isConfirmed ? <div className="flex items-center" data-id="element-61">
                  <CheckIcon size={16} className="text-green-600 mr-1" data-id="element-62" />
                  <span className="text-sm text-green-600" data-id="element-63">
                    Reader verified and ready for configuration
                  </span>
                </div> : <div className="flex items-center" data-id="element-64">
                  <span className="text-sm text-yellow-600" data-id="element-65">
                    Reader blinking - confirm if you see the LED flashing
                  </span>
                </div> : <span className="text-sm text-gray-600" data-id="element-66">
                Ping reader to verify before configuration
              </span>}
          </div>
          <div className="flex space-x-2" data-id="element-67">
            {reader.isBlinking ? <>
                {!reader.isConfirmed && <button onClick={handleConfirmClick} className="flex items-center bg-green-600 text-white px-3 py-1 rounded text-xs font-medium" data-id="element-68">
                    <CheckIcon size={14} className="mr-1" data-id="element-69" />
                    Confirm
                  </button>}
                <button onClick={handleStopBlinkingClick} className="flex items-center bg-gray-600 text-white px-3 py-1 rounded text-xs font-medium" data-id="element-70">
                  <XIcon size={14} className="mr-1" data-id="element-71" />
                  Stop
                </button>
              </> : <button onClick={handlePingClick} className="flex items-center bg-blue-600 text-white px-3 py-1 rounded text-xs font-medium" data-id="element-72">
                <RadioIcon size={14} className="mr-1" data-id="element-73" />
                Ping
              </button>}
          </div>
        </div>
      </div>
      {/* Expanded details */}
      {expanded && <div className="px-4 pb-4 border-t border-gray-100 pt-3" data-id="element-74">
          <div className="mb-4" data-id="element-75">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-76">
              Logical Reader
            </label>
            <select value={reader.logicalReaderId || ''} onChange={handleAssignReader} disabled={!reader.isConfirmed} className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm ${reader.isConfirmed ? 'focus:ring-blue-500 focus:border-blue-500' : 'bg-gray-100 cursor-not-allowed'}`} data-id="element-77">
              <option value="" data-id="element-78">-- Select Logical Reader --</option>
              {logicalReaders.map(lr => <option key={lr.id} value={lr.id} data-id="element-79">
                  {lr.name}
                </option>)}
            </select>
            {!reader.isConfirmed && <p className="mt-1 text-xs text-yellow-600" data-id="element-80">
                You must ping and confirm this reader before making changes
              </p>}
          </div>
          {assignedReader && <div className="space-y-2 text-sm" data-id="element-81">
              <div className="flex" data-id="element-82">
                <span className="text-gray-600 w-32" data-id="element-83">Door:</span>
                <span className="font-medium text-gray-900" data-id="element-84">
                  {assignedDoor?.name || 'None'}
                </span>
              </div>
              <div className="flex" data-id="element-85">
                <span className="text-gray-600 w-32" data-id="element-86">Door Control Unit:</span>
                <span className="font-medium text-gray-900" data-id="element-87">
                  {assignedReader.doorControlUnit}
                </span>
              </div>
              <div className="flex" data-id="element-88">
                <span className="text-gray-600 w-32" data-id="element-89">Vgunter Node:</span>
                <span className="font-medium text-gray-900" data-id="element-90">
                  {assignedReader.vgunterNode}
                </span>
              </div>
              <div className="flex" data-id="element-91">
                <span className="text-gray-600 w-32" data-id="element-92">Access Function:</span>
                <span className="font-medium text-gray-900" data-id="element-93">
                  {assignedReader.accessFunction}
                </span>
              </div>
            </div>}
        </div>}
    </div>;
};
export default ReaderItem;